import type { GoalOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Checkbox, Skeleton } from "@sagot/ui";
import { localized } from "./ranking";

interface GoalPickerProps {
  goals: readonly GoalOut[] | undefined;
  isPending: boolean;
  isError: boolean;
  value: readonly string[];
  onChange: (goals: string[]) => void;
}

/** Step 1, second question: what the assistant should do (multi-select, from template data). */
export function GoalPicker({ goals, isPending, isError, value, onChange }: GoalPickerProps) {
  const { t, i18n } = useTranslation();
  const toggle = (key: string, on: boolean) => {
    onChange(on ? [...value, key] : value.filter((goal) => goal !== key));
  };
  return (
    <fieldset className="space-y-3" data-testid="goal-picker">
      <legend className="text-base font-bold">{t("onboarding.org.goals")}</legend>
      <p className="text-sm text-fg-muted">{t("onboarding.org.goalsHint")}</p>
      {isPending ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" aria-hidden="true">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : isError || !goals ? (
        <p className="text-sm text-warning">{t("onboarding.org.goalsError")}</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {goals.map((goal) => (
            <div key={goal.key} className="rounded-lg border border-line p-3 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary-soft">
              <Checkbox
                checked={value.includes(goal.key)}
                onCheckedChange={(checked) => { toggle(goal.key, checked === true); }}
                label={<span className="font-semibold">{localized(goal.label, i18n.language)}</span>}
                description={localized(goal.description, i18n.language)}
              />
            </div>
          ))}
        </div>
      )}
    </fieldset>
  );
}
